import { Box } from "@mantine/core"
import { useEffect, useRef } from "react"
import { type IYouTubePlayer, loadYouTubeAPI } from "./youtube"

const BUFFER_TIMEOUT_MS = 15_000

interface IProps {
  videoId: string
  isPlaying: boolean
  onEnd?: () => void
}

export function YouTubePlayer({ videoId, isPlaying, onEnd }: IProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<IYouTubePlayer | null>(null)
  const isReadyRef = useRef(false)
  const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stateRef = useRef({
    videoId,
    isPlaying,
    onEnd,
  })

  const clearBufferTimeout = () => {
    if (bufferTimerRef.current !== null) {
      clearTimeout(bufferTimerRef.current)
      bufferTimerRef.current = null
    }
  }

  useEffect(() => {
    stateRef.current = {
      videoId,
      isPlaying,
      onEnd,
    }
  }, [videoId, isPlaying, onEnd])

  useEffect(() => {
    let destroyed = false

    loadYouTubeAPI()
      .then((YT) => {
        if (destroyed || !containerRef.current) return

        playerRef.current = new YT.Player(containerRef.current, {
          videoId: stateRef.current.videoId,
          playerVars: {
            autoplay: 0,
            disablekb: 1,
            rel: 0,
            playsinline: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: () => {
              if (destroyed || !playerRef.current) return

              isReadyRef.current = true

              // Safari: set allow attributes for autoplay + inline playback.
              const iframe = containerRef.current?.querySelector("iframe")
              if (iframe) {
                iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture")
                iframe.allowFullscreen = true
              }

              // Если videoId успел поменяться во время загрузки API.
              playerRef.current.loadVideoById(stateRef.current.videoId)

              if (stateRef.current.isPlaying) {
                playerRef.current.playVideo()
              }
            },

            onStateChange: (event) => {
              if (event.data === YT.PlayerState.BUFFERING && stateRef.current.isPlaying) {
                clearBufferTimeout()
                bufferTimerRef.current = setTimeout(() => {
                  stateRef.current.onEnd?.()
                }, BUFFER_TIMEOUT_MS)
              } else if (event.data === YT.PlayerState.PLAYING) {
                clearBufferTimeout()
              } else if (event.data === YT.PlayerState.ENDED) {
                clearBufferTimeout()
                stateRef.current.onEnd?.()
              } else if (event.data === YT.PlayerState.PAUSED) {
                clearBufferTimeout()
              }
            },

            onError: () => {
              clearBufferTimeout()
              stateRef.current.onEnd?.()
            },
          },
        })
      })
      .catch(() => {
        // ignore
      })

    return () => {
      destroyed = true
      isReadyRef.current = false
      clearBufferTimeout()

      try {
        playerRef.current?.destroy()
      } catch {
        // ignore
      }

      playerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!isReadyRef.current || !playerRef.current) return

    clearBufferTimeout()
    playerRef.current.loadVideoById(videoId)
  }, [videoId])

  useEffect(() => {
    if (!isReadyRef.current || !playerRef.current) return

    clearBufferTimeout()

    if (isPlaying) {
      playerRef.current.playVideo()
    } else {
      playerRef.current.pauseVideo()
    }
  }, [isPlaying])

  return <Box ref={containerRef} w="100%" h="100%" />
}
